/**
 * Layout-carrying design recipes.
 *
 * A design preset historically only set `--dl-*` tokens (color / type / form),
 * so "applying Bento" merely RECOLORED the same vertical stack. A *layout
 * recipe* closes that gap: it is a PURE tree transform that RESTRUCTURES the
 * page — it wraps the page's top-level blocks in a layout PRIMITIVE (`Grid` /
 * `Columns`) and assigns per-block `colSpan` / `rowSpan` (read generically by
 * the renderer off each child node's props) so the same content reads as a
 * bento mosaic, a magazine spread, a feature grid, or a stark stack.
 *
 * Design rules / guarantees:
 *  - PURE: `applyLayoutRecipe` returns a NEW tree and never mutates its input.
 *  - LOSSLESS: every existing block is preserved (blocks the recipe doesn't
 *    explicitly place fall into a default 1x1 cell). Content is never dropped.
 *  - IDEMPOTENT: if the page root already holds a single Grid/Columns child
 *    (i.e. a recipe was applied before), we RE-LAYOUT in place — unwrap its
 *    children and re-wrap once — rather than nesting a grid-in-a-grid.
 *  - SAFE: any unexpected/edge tree returns UNCHANGED (never throws).
 *  - REVERSIBLE: the `stack` recipe un-wraps back to a plain vertical column.
 *
 * Recipes operate on the ROOT container's direct children (the page blocks).
 * The root is normally a `Section`; we transform its `children` generically so
 * a `Container`/`Section` root both work.
 *
 * NB: this module imports only TYPES (erased at build) so it stays a tiny,
 * dependency-free, independently testable pure function. Node ids mirror
 * studio-ui `editor/treeOps#newNodeId` (`<slug>_<rand>`); the Grid/Columns
 * registry version is "0.1.0" (see components/src/registry.ts).
 */
import type { ComponentNode, ComponentTree } from "./tree.js";

/** Component names that lay their direct children out on a CSS grid. Mirrors
 *  the renderer's LAYOUT_PARENTS — these are the only wrappers a recipe emits,
 *  and the only single-child wrapper we treat as "already recipe-applied". */
const LAYOUT_CONTAINERS = new Set<string>(["Grid", "Columns"]);

/** Phase-1 layout primitives ship at this version (components/src/registry.ts). */
const LAYOUT_VERSION = "0.1.0";

/** The id of the special "reset to a single column" recipe (no wrapper). */
export const STACK_RECIPE_ID = "stack";

/** Per-block grid placement a recipe assigns by position. Values are the
 *  curated span enums the renderer accepts (colSpan 1–4, rowSpan 1–2). */
export type SpanAssign = { colSpan?: string; rowSpan?: string };

export interface LayoutRecipe {
  /** Stable recipe id (also stored on the emitted wrapper for provenance). */
  id: string;
  /** Human label, e.g. "Bento" — used to title the Apply-layout button. */
  label: string;
  /** One-line description of the structure it produces. */
  description: string;
  /** The wrapper primitive to emit and the props it carries. */
  container: { name: "Grid" | "Columns"; props: Record<string, unknown> };
  /** Spans for the block at `index` of `total` blocks. */
  spanFor: (index: number, total: number) => SpanAssign;
}

/* --------------------------------- recipes -------------------------------- */

const bento: LayoutRecipe = {
  id: "bento",
  label: "Bento",
  description:
    "A 4-column bento mosaic: the first block is a 2×2 focal tile, the rest alternate wide and narrow cells.",
  container: { name: "Grid", props: { columns: "4", gap: "md", align: "stretch" } },
  spanFor(index) {
    // First block = the hero focal tile (2 wide, 2 tall).
    if (index === 0) return { colSpan: "2", rowSpan: "2" };
    // Remaining blocks alternate wide (2) / narrow (1) for a mosaic rhythm.
    return { colSpan: index % 2 === 1 ? "2" : "1" };
  },
};

const editorial: LayoutRecipe = {
  id: "editorial",
  label: "Editorial",
  description:
    "A magazine spread: a full-width hero above a 2-column reading body, with the closing block full-width again.",
  container: { name: "Grid", props: { columns: "2", gap: "lg", align: "start" } },
  spanFor(index, total) {
    // Hero (first) and the closing block (last) run full width; the body in
    // between reads as two columns.
    if (index === 0) return { colSpan: "2" };
    if (total > 1 && index === total - 1) return { colSpan: "2" };
    return { colSpan: "1" };
  },
};

const brutalist: LayoutRecipe = {
  id: "brutalist",
  label: "Brutalist",
  description:
    "A stark, gapless single column: every block runs full width, hard-edged and flush.",
  // A 2-col grid where every block spans both columns => a full-width stack;
  // gap:none keeps blocks flush for the brutalist feel.
  container: { name: "Grid", props: { columns: "2", gap: "none", align: "stretch" } },
  spanFor() {
    return { colSpan: "2" };
  },
};

const featureGrid: LayoutRecipe = {
  id: "feature-grid",
  label: "Feature grid",
  description:
    "A full-width hero above an even three-column card grid — ideal for feature lists, services, or a gallery.",
  container: { name: "Grid", props: { columns: "3", gap: "md", align: "stretch" } },
  spanFor(index) {
    // Hero (first) spans the full width; everything after is an equal 1-col card.
    if (index === 0) return { colSpan: "3" };
    return { colSpan: "1" };
  },
};

const RECIPES: Record<string, LayoutRecipe> = { bento, editorial, brutalist, "feature-grid": featureGrid };

/** All authored recipes (id + label + description), for UI listing. Includes
 *  the synthetic "stack" reset which has no LayoutRecipe object of its own. */
export const LAYOUT_RECIPES: Array<Pick<LayoutRecipe, "id" | "label" | "description">> = [
  {
    id: STACK_RECIPE_ID,
    label: "Stack",
    description: "A clean single column — un-does any grid layout and lets every block run full width.",
  },
  ...Object.values(RECIPES).map(({ id, label, description }) => ({ id, label, description })),
];

/** Back-compat alias for the prior `listLayoutRecipes()` helper. */
export function listLayoutRecipes(): Array<Pick<LayoutRecipe, "id" | "label" | "description">> {
  return LAYOUT_RECIPES;
}

/** Look up a recipe by id, or undefined. (The "stack" reset is not in here.) */
export function getLayoutRecipe(recipeId: string): LayoutRecipe | undefined {
  return RECIPES[recipeId];
}

/* -------------------------------- internals ------------------------------- */

/** Mirror treeOps#newNodeId so inserted nodes read consistently. */
function newId(componentName: string): string {
  const slug = componentName.toLowerCase();
  const rand = Math.random().toString(36).slice(2, 7);
  return `${slug}_${rand}`;
}

/** Return a copy of `node` with any prior colSpan/rowSpan removed, so switching
 *  recipes (or re-applying) never leaves stale placement on a block. Returns
 *  the SAME reference when there's nothing to strip (lets callers detect no-ops). */
function stripSpans(node: ComponentNode): ComponentNode {
  if (node.props == null || (!("colSpan" in node.props) && !("rowSpan" in node.props))) {
    return node;
  }
  const props = { ...node.props };
  delete props.colSpan;
  delete props.rowSpan;
  return { ...node, props };
}

/** True when `node` is a layout wrapper a recipe could have emitted. */
function isLayoutWrapper(node: ComponentNode | undefined): node is ComponentNode {
  return !!node && LAYOUT_CONTAINERS.has(node.component?.name);
}

/** Resolve the page blocks to lay out: either the root's direct children, or —
 *  if the root holds exactly one prior Grid/Columns wrapper — that wrapper's
 *  children (so we re-layout in place instead of nesting). */
function resolveBlocks(root: ComponentNode): { blocks: ComponentNode[]; reuseWrapperId?: string } {
  const rootChildren = root.children ?? [];
  const lone = rootChildren.length === 1 ? rootChildren[0] : undefined;
  if (isLayoutWrapper(lone) && (lone.children?.length ?? 0) > 0) {
    return { blocks: lone.children ?? [], reuseWrapperId: lone.node_id };
  }
  return { blocks: rootChildren };
}

/** The "stack" reset: un-wrap a prior layout wrapper back to a plain vertical
 *  column and strip every block's spans. Returns the input UNCHANGED when the
 *  page is already a clean stack (no wrapper, no spans), so it never pollutes
 *  the undo history with a no-op. */
function resetToStack(tree: ComponentTree): ComponentTree {
  const root = tree.root;
  const rootChildren = root.children ?? [];
  if (rootChildren.length === 0) return tree;

  const lone = rootChildren.length === 1 ? rootChildren[0] : undefined;
  if (isLayoutWrapper(lone) && (lone.children?.length ?? 0) > 0) {
    // Unwrap: promote the wrapper's children to the root, spans stripped.
    const inner = (lone.children ?? []).map(stripSpans);
    return { root: { ...root, children: inner } };
  }
  // No wrapper — just clear any stray spans. No-op if there were none.
  let changed = false;
  const cleaned = rootChildren.map((b) => {
    const s = stripSpans(b);
    if (s !== b) changed = true;
    return s;
  });
  return changed ? { root: { ...root, children: cleaned } } : tree;
}

/* ------------------------------ the transform ----------------------------- */

/**
 * Apply a layout recipe to a page tree. PURE — returns a new tree (or the
 * input unchanged if it can't safely transform). Pass {@link STACK_RECIPE_ID}
 * ("stack") to reset to a plain single column.
 */
export function applyLayoutRecipe(tree: ComponentTree, recipeId: string): ComponentTree {
  try {
    if (!tree || !tree.root) return tree;
    if (recipeId === STACK_RECIPE_ID) return resetToStack(tree);

    const recipe = RECIPES[recipeId];
    if (!recipe) return tree;

    const root = tree.root;
    if ((root.children ?? []).length === 0) return tree;

    const { blocks, reuseWrapperId } = resolveBlocks(root);
    if (blocks.length === 0) return tree;

    // Re-assign spans fresh for every block (clearing any prior placement).
    const total = blocks.length;
    const laidOut: ComponentNode[] = blocks.map((block, index) => {
      const cleaned = stripSpans(block);
      const span = recipe.spanFor(index, total);
      const props: Record<string, unknown> = { ...cleaned.props };
      if (span.colSpan) props.colSpan = span.colSpan;
      if (span.rowSpan) props.rowSpan = span.rowSpan;
      return { ...cleaned, props };
    });

    const wrapper: ComponentNode = {
      node_id: reuseWrapperId ?? newId(recipe.container.name),
      component: { name: recipe.container.name, version: LAYOUT_VERSION },
      // `recipeId` is a provenance marker — not in the Grid/Columns schema, so
      // zod strips it before render, but it persists in the stored tree.
      props: { ...recipe.container.props, recipeId },
      children: laidOut,
    };

    return { root: { ...root, children: [wrapper] } };
  } catch {
    // Never throw on an unexpected tree — leave the page untouched.
    return tree;
  }
}
